import { Injectable } from '@angular/core';
import {
  ButtonColor,
  CommonActionKey,
  HorizontalListData,
  HorizontalListFactory,
  IdentityVerificationMethod,
  IdentityVerificationMethodLabel,
  IdentityVerificationStatus,
  IdentityVerificationStatusLabel,
  IdentityVerificationSummary,
  SimpleDialogData,
} from 'common';
import { CustomerStatus } from '../../customer.model';

export const VerificationStatusLabels = {
  [IdentityVerificationStatus.None]: {
    color: 'mat-grey-bg',
    icon: 'help',
    label: IdentityVerificationStatusLabel.get(IdentityVerificationStatus.None),
  },
  [IdentityVerificationStatus.Pending]: {
    color: 'mat-grey-bg',
    icon: 'help',
    label: IdentityVerificationStatusLabel.get(IdentityVerificationStatus.Pending),
  },
  [IdentityVerificationStatus.Syncing]: {
    color: 'mat-grey-bg',
    icon: 'help',
    label: IdentityVerificationStatusLabel.get(IdentityVerificationStatus.Syncing),
  },
  [IdentityVerificationStatus.Failed]: {
    color: 'mat-red-bg',
    icon: 'error',
    label: IdentityVerificationStatusLabel.get(IdentityVerificationStatus.Failed),
  },
  [IdentityVerificationStatus.Success]: {
    color: 'mat-green-bg',
    icon: 'check_circle',
    label: IdentityVerificationStatusLabel.get(IdentityVerificationStatus.Success),
  },
};

@Injectable()
export class CustomerIdentityVerificationWidgetAdapter {
  adaptListData = (data: IdentityVerificationSummary, canUseActionLinks: boolean, customerStatus: CustomerStatus): HorizontalListData[] => {
    const status = data?.status;
    const isIdVerseMethod = data?.method === IdentityVerificationMethod.IDVerse;
    const isRegistered = customerStatus === CustomerStatus.Registered;

    // Recovery actions are available whenever the backend will accept them: a registered
    // customer who is neither mid-attempt (Pending/Syncing) nor already verified (Success).
    // This matches the API guards (UpdateIdentityVerification/StartNewKBA require Registered),
    // not the previous Failed-only rule which hid the actions for a customer stuck at None.
    const canAct = canUseActionLinks
      && isRegistered
      && status !== IdentityVerificationStatus.Pending
      && status !== IdentityVerificationStatus.Syncing
      && status !== IdentityVerificationStatus.Success;

    return [
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Identity verification method',
        displayedValue: IdentityVerificationMethodLabel.get(data?.method),
      }),
      HorizontalListFactory.createKeyValueCustomValueTemplateItem({
        title: 'Verification status',
        customTemplateName: 'verificationStatusValue',
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Next step',
        displayedValue: 'Waiting for customer to register',
        isItemHidden: customerStatus !== CustomerStatus.Pending,
      }),
      HorizontalListFactory.createCustomTemplateItem({
        customTemplateName: 'startKbaActionItem',
        isItemHidden: !canAct || !isIdVerseMethod,
      }),
      HorizontalListFactory.createCustomTemplateItem({
        customTemplateName: 'startNewIdVerseActionItem',
        isItemHidden: !canAct,
      }),
      HorizontalListFactory.createCustomTemplateItem({
        customTemplateName: 'setToVerifiedActionItem',
        isItemHidden: !canAct,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Last error message',
        displayedValue: data?.lastErrorMessage,
      }),
      HorizontalListFactory.createKeyValueStringItem({
        title: 'Errors',
        isValueHidden: !data?.errors,
        displayedValue: Object.values(data?.errors ?? {}).flat().join('\n'),
      }),
    ];
  };
}

export const getStartKbaDialogData = (): SimpleDialogData => {
  return {
    title: 'Confirm action?',
    body: '<b style="font-weight: 500;">Start KBA Verification</b> switches the verification method to KBA but does not modify the existing KBA configuration. Previous verification attempts will remain in the Activity section.',
    actions: [
      { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
      { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
    ],
  };
};

export const getStartNewIdVerseDialogData = (): SimpleDialogData => {
  return {
    title: 'Confirm action?',
    body: '<b style="font-weight: 500;">Start New IDVerse Verification</b> gives the customer another chance to verify. Previous verification attempts will remain in the Activity section.',
    actions: [
      { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
      { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
    ],
  };
};

export const getSetToVerifiedDialogData = (): SimpleDialogData => {
  return {
    title: 'Confirm action?',
    body: '<b style="font-weight: 500;">Set Status to "Verified"</b> overrides IDVerse and/or KBA verification results and sets the status to Verified, allowing the user to continue with onboarding. Use this option only when offline verification has been performed.',
    actions: [
      { label: 'CANCEL', buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
      { label: 'CONFIRM', buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
    ],
  };
};